import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Save, FileCheck } from "lucide-react";
import { CERT_TYPES, prefillValue } from "../lib/certificateTypes";

export default function CertificateForm() {
  const { pid, type } = useParams();
  const nav = useNavigate();
  const cfg = CERT_TYPES[type];
  const [patient, setPatient] = useState(null);
  const [snapshot, setSnapshot] = useState({});
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cfg) return;
    (async () => {
      try {
        const [p, latest, labs] = await Promise.all([
          api.get(`/patients/${pid}`),
          api.get(`/patients/${pid}/latest`).catch(() => ({ data: {} })),
          api.get(`/patients/${pid}/latest-investigations`).catch(() => ({ data: { latest: {} } })),
        ]);
        setPatient(p.data.patient);
        const v = latest.data?.visit || {};
        const ctx = {
          vitals: v.vitals || {},
          diagnosis: v.diagnosis || {},
          medicines: v.medicines || [],
          known_case_of: v.known_case_of || [],
          labs: labs.data?.latest || {},
        };
        setSnapshot(ctx);
        // Seed initial fields with prefill values.
        const initial = {};
        for (const s of cfg.sections) {
          for (const f of s.fields) {
            if (f.source) {
              const v2 = prefillValue(f.source, ctx);
              if (v2 !== "" && v2 != null) initial[f.name] = String(v2);
            }
          }
        }
        // Sensible defaults
        if (!initial.test_date) initial.test_date = new Date().toISOString().slice(0, 10);
        if (cfg.key === "medical" && !initial.rest_from) initial.rest_from = new Date().toISOString().slice(0, 10);
        setFields(initial);
      } catch (e) {
        console.warn("cert prefill failed:", e);
        toast.error("Could not load patient");
      }
    })();
  }, [pid, type, cfg]);

  const patientHead = useMemo(() => ({
    name: patient?.name || "",
    age: patient?.age || "",
    sex: patient?.sex || "",
  }), [patient]);

  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const r = await api.post("/certificates", { patient_id: pid, type, fields });
      const cid = r.data?.certificate?.certificate_id;
      if (cid) {
        toast.success(`${cfg.label} saved`);
        nav(`/certificate/print/${cid}`);
      } else {
        throw new Error("no certificate_id in response");
      }
    } catch (e) {
      console.warn("cert save failed:", e);
      toast.error(e?.response?.data?.detail || "Could not save certificate");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return <div className="p-8 text-sm text-rose-700">Unknown certificate type: {type}</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="cert-back-btn"><ArrowLeft size={14} className="mr-1" /> Back</Button>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">{patientHead.name} · {patientHead.age} · {patientHead.sex}</div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><FileCheck size={20} className="text-pink-600" /> {cfg.label}</h1>
          </div>
          <div className="ml-auto">
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="cert-save-btn">
              <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save & Print"}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm space-y-6">
          <div className="text-sm text-slate-700 italic border-l-4 border-pink-200 pl-3">{cfg.intro(patientHead)}</div>
          {cfg.sections.map((s, si) => (
            <section key={si} data-testid={`cert-section-${si}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{s.title}</div>
              <div className="grid grid-cols-3 gap-3">
                {s.fields.map((f) => (
                  <div key={f.name} className={`col-span-${f.cols || 1}`} style={{ gridColumn: `span ${f.cols || 1} / span ${f.cols || 1}` }}>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-0.5">{f.label}</label>
                    {f.type === "textarea" ? (
                      <Textarea rows={2} value={fields[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`cert-field-${f.name}`} />
                    ) : f.type === "select" ? (
                      <select
                        className="h-9 w-full border border-slate-200 rounded px-2 text-sm"
                        value={fields[f.name] || ""}
                        onChange={(e) => set(f.name, e.target.value)}
                        data-testid={`cert-field-${f.name}`}
                      >
                        <option value="">Select…</option>
                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <Input
                        type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                        value={fields[f.name] || ""}
                        onChange={(e) => set(f.name, e.target.value)}
                        data-testid={`cert-field-${f.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
